import { ActionTree } from 'vuex';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { DocumentService } from '@/api/documents/services/document.service';
import { IImplementResourceDTO } from '@/api/resources/dtos/implement-resource-dto';
import { ResourceService } from '@/api/resources/services/resource.service';
import {
  EDisconnectResourceAction,
  EDisconnectResourceMutation,
  state as defaultState,
} from '@/content-library/disconnect-resource/store/disconnect-resource.constants';
import { IDisconnectResourceState } from '@/content-library/disconnect-resource/store/disconnect-resource.types';
import { ContentLibraryHelper } from '@/content-library/helpers/content-library.helper';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { EditorBlockService } from '@/editor/api/services/editor-block.service';
import { IEditorBlock } from '@/editor/types';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';

export const actions: ActionTree<IDisconnectResourceState, RootState> = {
  async [EDisconnectResourceAction.Disconnect](
    { commit, dispatch },
    {
      documentId,
      page,
    }: {
      documentId: string;
      page: IDocumentPage;
    }
  ) {
    commit(EDisconnectResourceMutation.SetDisconnecting, true);
    try {
      const payload: IImplementResourceDTO = {
        documentId,
        resourceId: page.resourceId,
      };
      await ResourceService.disconnect(payload);
      dispatch(EDisconnectResourceAction.UpdatePage, page);
    } catch (error) {
      dispatch(EDisconnectResourceAction.DisconnectFailed);
    }
  },
  [EDisconnectResourceAction.DisconnectFailed]({ dispatch, commit }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-disconnect-resource'),
      { root: true }
    );
    commit(EDisconnectResourceMutation.SetNewDocumentPage, { ...defaultState.newDocumentPage });
  },
  async [EDisconnectResourceAction.UpdatePage]({ dispatch }, page: IDocumentPage) {
    try {
      let copy: IContentLinkBlock | IEditorBlock;
      switch (page.pageType) {
        case EDocumentPageType.ContentLink:
          copy = await ContentLinkBlockService.copyContentLink(page.contentLinkBlock.id);
          break;
        case EDocumentPageType.Editor:
          copy = await EditorBlockService.copyEditorBlock(page.editorBlock.id);
          break;
      }
      const payloadUpdatePage = ContentLibraryHelper.getUpdatePagePayload(page, copy.id);
      const updatedPage: IDocumentPage = await DocumentService.updatePage(payloadUpdatePage);
      dispatch(EDisconnectResourceAction.UpdatePageSuccess, updatedPage);
    } catch (e) {
      dispatch(EDisconnectResourceAction.UpdatePageFailed);
    }
  },
  [EDisconnectResourceAction.UpdatePageSuccess]({ commit }, updatedPage: IDocumentPage) {
    commit(EDisconnectResourceMutation.SetNewDocumentPage, updatedPage);
  },
  [EDisconnectResourceAction.UpdatePageFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-update-page'), {
      root: true,
    });
  },
  [EDisconnectResourceAction.Reset]({ commit }) {
    commit(EDisconnectResourceMutation.SetState, { ...defaultState });
  },
};
